import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  contractsApi,
  CreateContractExtensionDto,
  CreateContractRevisionDto,
  ContractFilterDto,
  CreateContractDto,
  UpdateContractConsumptionDto,
  UpdateContractDto,
} from '../../../api/contracts';
import { notifications } from '@mantine/notifications';

export const useContracts = (filters?: ContractFilterDto) => {
  return useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => contractsApi.list(filters),
  });
};

export const useContract = (id: string) => {
  return useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsApi.get(id),
    enabled: !!id,
  });
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContractDto) => contractsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      notifications.show({
        title: 'Success',
        message: 'Contract created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create contract',
        color: 'red',
      });
    },
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContractDto }) =>
      contractsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Contract updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update contract',
        color: 'red',
      });
    },
  });
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contractsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      notifications.show({
        title: 'Success',
        message: 'Contract deleted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to delete contract',
        color: 'red',
      });
    },
  });
};

export const useContractDashboardStats = () => {
  return useQuery({
    queryKey: ['contractDashboardStats'],
    queryFn: () => contractsApi.getDashboardStats(),
  });
};

export const useBulkImportContracts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun?: boolean }) =>
      contractsApi.bulkImport(file, dryRun),
    onSuccess: (result, variables) => {
      if (!variables.dryRun) {
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
        queryClient.invalidateQueries({ queryKey: ['contractDashboardStats'] });
        notifications.show({
          title: 'Import complete',
          message: `${result.created} created, ${result.skipped} skipped, ${result.errors} errors`,
          color: result.errors > 0 ? 'orange' : 'green',
        });
      }
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Import failed',
        message: error.response?.data?.error || 'Failed to import contracts',
        color: 'red',
      });
    },
  });
};

export const useUpdateConsumedValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      amount,
      poNumber,
      contractDocumentId,
    }: {
      id: string;
      amount: number;
      poNumber: string;
      contractDocumentId: string;
    }) => contractsApi.updateConsumedValue(id, { amount, poNumber, contractDocumentId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['contractDashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['contractConsumptions', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Consumed value updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update consumed value',
        color: 'red',
      });
    },
  });
};

export const useUpdateContractConsumption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      consumptionId,
      data,
    }: {
      id: string;
      consumptionId: string;
      data: UpdateContractConsumptionDto;
    }) => contractsApi.updateConsumption(id, consumptionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['contractDashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['contractConsumptions', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['contractAuditLogs', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'PO consumption updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update PO consumption',
        color: 'red',
      });
    },
  });
};

export const useContractConsumptions = (id: string) => {
  return useQuery({
    queryKey: ['contractConsumptions', id],
    queryFn: () => contractsApi.listConsumptions(id),
    enabled: !!id,
  });
};

export const useCreateContractExtension = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateContractExtensionDto }) =>
      contractsApi.createExtension(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['contractExtensions', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['contractAuditLogs', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Contract extended successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to extend contract',
        color: 'red',
      });
    },
  });
};

export const useContractExtensions = (id: string) => {
  return useQuery({
    queryKey: ['contractExtensions', id],
    queryFn: () => contractsApi.listExtensions(id),
    enabled: !!id,
  });
};

export const useCreateContractRevision = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateContractRevisionDto }) =>
      contractsApi.createRevision(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['contractDashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['contractRevisions', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['contractAuditLogs', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Contract value revised successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to revise contract value',
        color: 'red',
      });
    },
  });
};

export const useContractRevisions = (id: string) => {
  return useQuery({
    queryKey: ['contractRevisions', id],
    queryFn: () => contractsApi.listRevisions(id),
    enabled: !!id,
  });
};

export const useContractAuditLogs = (id: string) => {
  return useQuery({
    queryKey: ['contractAuditLogs', id],
    queryFn: () => contractsApi.listAuditLogs(id),
    enabled: !!id,
  });
};

